import React from "react";
import { Check } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

export default function Pricing() {
  const { t } = useLanguage();

  const PLANS = [
    {
      key: "explorer",
      name: t("pricing.explorer_name"),
      price: "0",
      tag: t("pricing.explorer_tag"),
      features: [
        t("pricing.explorer_f1"),
        t("pricing.explorer_f2"),
        t("pricing.explorer_f3"),
        t("pricing.explorer_f4"),
      ],
      cta: t("pricing.explorer_cta"),
      highlight: false,
    },
    {
      key: "author",
      name: t("pricing.author_name"),
      price: "9",
      tag: t("pricing.author_tag"),
      features: [
        t("pricing.author_f1"),
        t("pricing.author_f2"),
        t("pricing.author_f3"),
        t("pricing.author_f4"),
        t("pricing.author_f5"),
      ],
      cta: t("pricing.author_cta"),
      highlight: true,
    },
    {
      key: "studio",
      name: t("pricing.studio_name"),
      price: "29",
      tag: t("pricing.studio_tag"),
      features: [
        t("pricing.studio_f1"),
        t("pricing.studio_f2"),
        t("pricing.studio_f3"),
        t("pricing.studio_f4"),
        t("pricing.studio_f5"),
      ],
      cta: t("pricing.studio_cta"),
      highlight: false,
    },
  ];

  return (
    <section
      id="precios"
      data-testid="pricing-section"
      className="relative border-t border-zinc-200 bg-zinc-50/40"
    >
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
        <div className="max-w-2xl mb-16">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">
            {t("pricing.kicker")}
          </span>
          <h2 className="mt-3 font-serif text-4xl sm:text-5xl leading-[1.05] tracking-tight text-zinc-950">
            {t("pricing.title_part1")}
            <span className="italic text-[#002FA7]">{t("pricing.title_emphasis")}</span>
            {t("pricing.title_end")}
          </h2>
          <p className="mt-4 font-sans text-base text-zinc-600 leading-relaxed">
            {t("pricing.subtitle")}
          </p>
          <div
            data-testid="pricing-beta-banner"
            className="mt-6 inline-flex items-center gap-3 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] font-semibold text-emerald-800">
              Beta abierta · Todo gratis durante el lanzamiento
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PLANS.map((p) => (
            <div
              key={p.key}
              data-testid={`pricing-card-${p.key}`}
              className={`relative flex flex-col rounded-2xl p-8 transition-colors duration-300 ${
                p.highlight
                  ? "bg-[#002FA7] text-white border border-[#002FA7]"
                  : "bg-white border border-zinc-200 hover:border-zinc-300"
              }`}
            >
              {p.highlight && (
                <span className="absolute top-4 right-4 rounded-full bg-white/15 px-3 py-1 font-mono text-[10px] uppercase tracking-widest">
                  {t("pricing.popular")}
                </span>
              )}
              <span
                className={`font-mono text-[11px] uppercase tracking-[0.2em] font-semibold ${
                  p.highlight ? "text-white/70" : "text-zinc-500"
                }`}
              >
                {p.name}
              </span>
              <div className="mt-6 flex items-end gap-1">
                <span className="font-serif text-6xl leading-none">
                  {p.price === "0" ? "$0" : `$${p.price}`}
                </span>
                <span
                  className={`mb-2 font-sans text-sm ${
                    p.highlight ? "text-white/70" : "text-zinc-500"
                  }`}
                >
                  {t("pricing.perMonth")}
                </span>
              </div>
              <p
                className={`mt-2 font-sans text-sm ${
                  p.highlight ? "text-white/80" : "text-zinc-600"
                }`}
              >
                {p.tag}
              </p>

              <ul className="mt-8 space-y-3 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5">
                    <Check
                      className={`h-4 w-4 mt-0.5 shrink-0 ${
                        p.highlight ? "text-white" : "text-[#002FA7]"
                      }`}
                    />
                    <span
                      className={`font-sans text-sm ${
                        p.highlight ? "text-white/90" : "text-zinc-700"
                      }`}
                    >
                      {f}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                data-testid={`pricing-cta-${p.key}`}
                onClick={() => {
                  if (p.key === "explorer") {
                    // Free tier — jump to the top of the app.
                    document
                      .getElementById("audio-config-panel")
                      ?.scrollIntoView({ behavior: "smooth" });
                    document
                      .querySelector('[data-testid="hero-section"]')
                      ?.scrollIntoView({ behavior: "smooth" });
                  } else {
                    // Paid tiers: not live yet — open a mailto to collect
                    // interest without misleading the visitor.
                    window.location.href =
                      "mailto:audioflip01@gmail.com?subject=" +
                      encodeURIComponent(`Interés en el plan ${p.name}`) +
                      "&body=" +
                      encodeURIComponent(
                        "Hola, me interesa el plan " +
                          p.name +
                          " cuando esté disponible. Por favor avísenme en el lanzamiento."
                      );
                  }
                }}
                className={`mt-8 h-11 rounded-md font-sans text-sm font-semibold transition-colors duration-200 ${
                  p.highlight
                    ? "bg-white text-[#002FA7] hover:bg-zinc-100"
                    : "bg-zinc-950 text-white hover:bg-zinc-800"
                }`}
              >
                {p.key === "explorer" ? p.cta : "Notifícame en el lanzamiento"}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
